import { useEffect, useState } from 'react';
import { REWARD_TIERS, menuItems, referralRewardText, type LineReward } from '@tikkas/shared';
import { ART, IC, Svg } from '../art/icons.js';
import { api, ApiError } from '../lib/api.js';
import { num } from '../lib/format.js';
import { useNav } from '../lib/router.js';
import { emailBonusToCome, featureOn, freePoints, freeSideChoices, rewardPoints, useStore } from '../lib/store.js';
import { Sheet } from './ui.js';
import { WalletButtons } from './Wallet.js';
import { useCard } from './TikkasCard.js';
import type { OffersDto, OfferVoucher } from '../lib/types.js';
import { playGame } from '../lib/games.js';
import { earnRows, gameRewardLine, nextReward, rewardTiers } from '../lib/offers.js';
import { useLook } from '../lib/look.js';
import { claimMessage } from '../lib/joining.js';

/*
 * The sheets Home's card and pills open (CLAUDE.md §4, Saif 21 Oct 2026 evening; the Offers page's content lives here now):
 *  - the card's menu (three dots): Activity, Add to Wallet, how points work;
 *  - Rewards: the next free item, the rewards ready to claim, vouchers (a friend's reward, codes, spin prizes), "Got a code?";
 *  - Earn: invite a friend, spin the wheel when one is waiting, add your email for bonus points, play and earn, points from a receipt.
 */

type Entry = { open: boolean; text: string; busy: boolean; err: string | null };
const closed: Entry = { open: false, text: '', busy: false, err: null };

/** GET /api/me/offers; null before it answers (the sheet then uses what /api/me has). */
function useOffers(s: ReturnType<typeof useStore>): [OffersDto | null, () => void] {
  const [offers, setOffers] = useState<OffersDto | null>(null);
  const [n, setN] = useState(0);
  const u = s.me?.customer;
  useEffect(() => {
    if (!u) return setOffers(null);
    api<OffersDto>('/api/me/offers').then(setOffers).catch(() => setOffers(null));
  }, [u?.id, u?.points, u?.vouchers.length, n]); // eslint-disable-line react-hooks/exhaustive-deps
  return [offers, () => setN((x) => x + 1)];
}

function EntryRow({ e, set, title, sub, placeholder, label, submit, icon }: { e: Entry; set: (x: Entry) => void; title: string; sub: string; placeholder: string; label: string; submit: () => void; icon: string }) {
  if (e.open) {
    return (
      <div className="r open"><div className="col" style={{ gap: 8, width: '100%' }}>
        <form className="codebox" onSubmit={(ev) => { ev.preventDefault(); submit(); }}><input type="text" autoComplete="off" autoCapitalize="characters" placeholder={placeholder} aria-label={label} value={e.text} onChange={(ev) => set({ ...e, text: ev.target.value, err: null })} autoFocus /><button type="submit" disabled={e.busy}>{e.busy ? '…' : 'Add'}</button></form>
        {e.err ? <span className="err" role="alert">{e.err}</span> : <span className="small muted">{sub}</span>}
      </div></div>
    );
  }
  return <button type="button" className="rowbtn" onClick={() => set({ ...closed, open: true })}><span className="ic"><Svg html={icon} /></span><span className="grow col" style={{ gap: 2 }}><b>{title}</b><span className="s">{sub}</span></span><Svg html={IC.chev} /></button>;
}

/** The card's menu: Activity, the Wallet badge (Apple's and Google's own artwork, so their rules hold inside a menu too), how points work. */
export function CardMenuSheet() {
  const s = useStore();
  const { navigate } = useNav();
  const card = useCard(s);
  const r = s.config?.rewards;
  return (
    <Sheet onClose={s.closeSheet}>
      <h2 className="h2">Your card</h2>
      <section className="card rows">
        <button type="button" onClick={() => navigate('/card/activity')}><span className="ic"><Svg html={IC.clock} /></span><span className="grow col" style={{ gap: 2 }}><b>Activity</b><span className="s">Every point and every penny of credit, newest first</span></span><Svg html={IC.chev} /></button>
        <div className="r"><span className="ic"><Svg html={IC.star} /></span><span className="grow col" style={{ gap: 2 }}><b>How points work</b><span className="s">{num(r?.pointsPerPound.card ?? 5)} points per £1 you spend</span><span className="s">{num(r?.pointsPerPound.credit ?? 10)} per £1 when you pay with store credit</span></span></div>
      </section>
      {featureOn(s, 'wallet') ? <div className="col" style={{ alignItems: 'center', gap: 8 }}><WalletButtons server={card ? { apple: card.wallet.apple, google: card.wallet.google } : null} onDevice={card?.wallet.onDevice ?? null} last4={card?.wallet.codeLast4 ?? null} /></div> : null}
      <button type="button" className="plain" onClick={s.closeSheet}>Close</button>
    </Sheet>
  );
}

export function RewardsSheet() {
  const s = useStore();
  const { t } = useLook();
  const u = s.me?.customer;
  const cfg = s.config?.rewards;
  const tiers = cfg?.tiers ?? REWARD_TIERS;
  const rp = rewardPoints(s);
  const menu = s.menuData?.menu;
  const [offers, reload] = useOffers(s);
  const [code, setCode] = useState<Entry>(closed);
  const fp = freePoints(s);
  const next = nextReward(fp, rp);
  const views = rewardTiers(fp, tiers);
  const use = (id: 'side' | 'main') => {
    const reward: LineReward = { kind: 'points', rewardId: id, points: rp[id] };
    if (id === 'main') s.openSheet({ kind: 'pickMain', reward });
    else s.openSheet({ kind: 'pickSide', reward, dessertsOnly: false });
  };
  const vouchers: OfferVoucher[] = offers?.vouchers ?? (u?.vouchers ?? []).map((v) => ({ ...v, code: null }));
  const voucherItem = (v: OfferVoucher): string | null => (v.kind !== 'referral_item' ? null : v.itemId && menu?.items[v.itemId] ? v.itemId : s.config?.referral?.itemId ?? null);
  const voucherReady = (v: OfferVoucher): boolean => {
    if (v.kind === 'code') return true;
    if (!menu) return false;
    if (v.kind === 'spin_dessert') return menuItems(menu, 'desserts').some((d) => !d.soldOut);
    const it = menu.items[voucherItem(v) ?? ''];
    return !!it && !it.soldOut;
  };
  const useVoucher = async (v: OfferVoucher) => {
    if (v.kind === 'spin_dessert') return s.openSheet({ kind: 'pickSide', reward: { kind: 'spin', prize: 'dessert' }, dessertsOnly: true });
    if (v.kind === 'code') {
      if (v.code && (await s.applyCode(v.code))) s.toast(`${v.code} is on your next order`);
      return;
    }
    const itemId = voucherItem(v);
    if (!itemId || !u) return;
    const inBag = s.bag.filter((l) => l.config.reward?.kind === 'referral' && l.config.itemId === itemId).reduce((a, l) => a + l.qty, 0);
    if (inBag >= vouchers.filter((x) => x.kind === 'referral_item' && voucherItem(x) === itemId).length) return s.toast('Already in your bag');
    s.addLine({ itemId, sizeIndex: 0, portions: [], sauces: [], toppingsOff: [], upgradeIds: [], reward: { kind: 'referral' } });
    s.toast('Added to your bag · free when you collect');
    s.closeSheet();
  };
  const saveCode = async () => {
    const text = code.text.trim();
    if (!text) return setCode({ ...code, err: 'Type a code first.' });
    setCode({ ...code, busy: true, err: null });
    try {
      const r = await api<{ ok: boolean; message?: string }>('/api/me/offers/codes', { body: { code: text } });
      if (!r.ok) return setCode({ ...code, busy: false, err: r.message ?? "That code isn't valid." });
      setCode(closed);
      s.toast('Code saved to your vouchers');
      reload();
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        const ok = await s.applyCode(text);
        return setCode(ok ? closed : { ...code, busy: false, err: s.codeError || "That code isn't valid." });
      }
      setCode({ ...code, busy: false, err: e instanceof Error ? e.message : 'Could not check that code' });
    }
  };
  const removeCode = async (v: OfferVoucher) => {
    await api(`/api/me/offers/codes/${encodeURIComponent(v.id)}`, { method: 'DELETE' }).catch(() => undefined);
    reload();
  };
  return (
    <Sheet onClose={s.closeSheet}>
      <h2 className="h2">{t('offers.rewards')}</h2>
      {next ? (
        <div className="nextbar" aria-label={`${num(next.left)} points to ${next.what}`}>
          <span className="small"><b className="tnum">{num(next.left)}</b> points to {next.what}</span>
          <span className="rail3"><i style={{ width: `${next.pct}%` }} /></span>
        </div>
      ) : null}
      <section className="card rows" aria-label={t('offers.rewards')}>
        {views.map((tier) => (
          <div className="r" key={tier.id}><span className="ic"><span className="ricon"><Svg html={tier.id === 'main' ? ART.cream! : ART.tots!} /></span></span><span className="grow col" style={{ gap: 3 }}><b>{tier.name}</b><span className="s">{num(tier.points)} points · {tier.id === 'side' ? freeSideChoices(s) : 'Any Regular bowl, wrap or Just Tikkas. Extras are charged.'}</span></span>{tier.ready ? <button type="button" className="pillbtn line" onClick={() => use(tier.id)}>Use</button> : <span className="small muted tnum togo">{num(tier.toGo)} to go</span>}</div>
        ))}
      </section>
      <h3 className="h3" style={{ margin: 0 }}>{t('offers.vouchers')}</h3>
      <section className="card rows" aria-label={t('offers.vouchers')}>
        {vouchers.map((v) => (
          <div className="r" key={v.id}><span className="ic">{v.kind === 'code' ? <Svg html={IC.tag} /> : <span className="ricon"><Svg html={v.kind === 'spin_dessert' ? ART.gulab! : ART.tots!} /></span>}</span>
            <span className="grow col" style={{ gap: 3 }}><b>{v.label}</b><span className="s">{v.kind === 'referral_item' && voucherItem(v) && menu?.items[voucherItem(v)!] ? `Free ${menu.items[voucherItem(v)!]!.name} · ` : ''}{v.kind === 'code' && s.code?.code === v.code ? 'On your next order · ' : ''}{v.expiresAt ? `Use by ${new Date(v.expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : 'Yours to use any time'}</span></span>
            {v.kind === 'code' && s.code?.code === v.code ? <button type="button" className="pillbtn" onClick={() => s.removeCode()}>Take off</button> : voucherReady(v) ? <button type="button" className="pillbtn line" onClick={() => useVoucher(v)}>Use</button> : <span className="small muted togo">Not available today</span>}
            {v.kind === 'code' && s.code?.code !== v.code ? <button type="button" className="iconbtn" aria-label={`Remove ${v.code}`} onClick={() => removeCode(v)}><Svg html={IC.close} /></button> : null}
          </div>
        ))}
        {s.code && !vouchers.some((v) => v.kind === 'code' && v.code === s.code?.code) && <div className="r"><span className="ic"><Svg html={IC.tag} /></span><span className="grow col" style={{ gap: 3 }}><b>{s.code.label}</b><span className="s">Code {s.code.code} · on your next order</span></span><button type="button" className="pillbtn" onClick={() => s.removeCode()}>Take off</button></div>}
        <EntryRow e={code} set={setCode} title="Got a code?" sub="From a friend, your office or a creator" placeholder="e.g. ACME5" label="Code" submit={saveCode} icon={IC.tag} />
      </section>
      <button type="button" className="plain" onClick={s.closeSheet}>Close</button>
    </Sheet>
  );
}

export function EarnSheet() {
  const s = useStore();
  const { navigate } = useNav();
  const { t } = useLook();
  const [offers] = useOffers(s);
  const [claim, setClaim] = useState<Entry>(closed);
  const inv = offers?.invite;
  const spinOrder = [...(s.me?.liveOrders ?? []), ...(s.me?.lastOrder ? [s.me.lastOrder] : [])].find((o) => o.spin.status === 'ready');
  const spinOn = featureOn(s, 'spin');
  const rows = earnRows({ spinWaiting: spinOn && !!spinOrder, emailBonusPoints: emailBonusToCome(s), gamesOn: featureOn(s, 'games'), gamesCount: offers?.games.length ?? 0 });
  const claimReceipt = async () => {
    const text = claim.text.trim();
    if (!text) return setClaim({ ...claim, err: 'Type the code from your receipt.' });
    setClaim({ ...claim, busy: true, err: null });
    try {
      const r = await api<{ ok: boolean; pointsAdded: number; receiptPoints?: number; welcomePoints?: number }>('/api/me/claims', { body: { code: text } });
      setClaim(closed);
      s.toast(claimMessage(r));
      await s.refreshMe();
    } catch (e) {
      setClaim({ ...claim, busy: false, err: e instanceof ApiError && e.status === 404 && e.code !== 'claim_unknown' ? 'Receipt codes open soon.' : e instanceof Error ? e.message : 'Could not claim those points' });
    }
  };
  const go = (path: string) => navigate(path); // pushed over the sheet's entry: Back (the left-edge swipe) returns to Earn
  return (
    <Sheet onClose={s.closeSheet}>
      <h2 className="h2">Earn</h2>
      <section className="card rows">
        {rows.map((id) => {
          if (id === 'invite') return <button type="button" key={id} onClick={() => go('/invite')}><span className="ic"><Svg html={IC.gift} /></span><span className="grow col" style={{ gap: 2 }}><b>Invite a friend</b><span className="s">You both get {referralRewardText(inv?.reward ?? s.config?.referral)}{inv && inv.joined ? ` · ${num(inv.joined)} joined, ${num(inv.ordered)} ordered` : ''}{inv?.code ? ` · your code ${inv.code}` : ''}</span></span><Svg html={IC.chev} /></button>;
          if (id === 'spin') return <button type="button" key={id} onClick={() => go(`/order/${spinOrder!.id}`)}><span className="ic"><Svg html={IC.spark} /></span><span className="grow col" style={{ gap: 2 }}><b>Spin the wheel</b><span className="s">A free spin is waiting on order #{spinOrder!.number}. Every spin wins.</span></span><Svg html={IC.chev} /></button>;
          if (id === 'email') return <button type="button" key={id} onClick={() => s.openSheet({ kind: 'emailList' })}><span className="ic"><Svg html={IC.bell} /></span><span className="grow col" style={{ gap: 2 }}><b>{t('earn.email_list').replace('{bonus}', num(emailBonusToCome(s)))}</b><span className="s">Offers and news now and then. Unsubscribe any time.</span></span><Svg html={IC.chev} /></button>;
          if (id === 'receipt') return <EntryRow key={id} e={claim} set={setClaim} title="Points from a receipt" sub="Bought in the shop before you joined? Type the code under the receipt's QR." placeholder="ABCDE-FGHJK" label="Receipt code" submit={claimReceipt} icon={IC.rewards} />;
          return null;
        })}
      </section>
      {rows.includes('games') ? <PlayAndEarn title={t('offers.games')} games={offers?.games ?? null} /> : null}
      <button type="button" className="plain" onClick={s.closeSheet}>Close</button>
    </Sheet>
  );
}

/**
 * Play and earn (games switched on): the games the server lists, each linked to this account when it opens. Points for
 * playing, never for downloading. Nothing shows until the server has answered, so no stand-in game ever reaches a customer.
 */
function PlayAndEarn({ title, games }: { title: string; games: OffersDto['games'] | null }) {
  const s = useStore();
  const [busy, setBusy] = useState<string | null>(null);
  if (!games?.length) return null;
  const play = async (id: string) => {
    setBusy(id);
    const err = await playGame(id);
    setBusy(null);
    if (err) s.toast(err);
  };
  return (
    <section className="sec"><h3 className="h3" style={{ margin: 0 }}>{title}</h3>
      {games.map((g) => (
        <div className="card gamecard" key={g.id}>
          {g.imageUrl ? <img className="gameicon" src={g.imageUrl} alt="" /> : <span className="gameicon"><Svg html={IC.game} /></span>}
          <span className="grow col" style={{ gap: 3, minWidth: 0 }}>
            <b style={{ fontSize: 16 }}>{g.name}{g.playsToday > 0 ? <span className="badge" style={{ marginLeft: 8 }}>Played today</span> : null}</b>
            <span className="small muted">{g.blurb ? `${g.blurb} ` : ''}{gameRewardLine(g.reward)} It opens linked to your Tikkas account.</span>
          </span>
          <button type="button" className="pillbtn line" disabled={busy !== null} onClick={() => play(g.id)}>{busy === g.id ? 'One moment…' : 'Play'}</button>
        </div>
      ))}
    </section>
  );
}
